import usePrevious from 'hooks/usePrevious';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, REMOVE_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';

const useNonMonetaryData = ({ formId = 1, siteInfoId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const serverTableKey = 'support';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const dispatch = useDispatch();
    const [rows, setRows] = useState(remoteData);
    const previousData = usePrevious(remoteData);

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const removeAllTempData = () => {
        setRows(
            rows.filter((item) => {
                return !item.id.toString().startsWith('temp_');
            }),
        );
    };

    const [choice] = useState({
        type: [
            { key: 0, label: 'Drug' },
            { key: 1, label: 'Consumable' },
            { key: 2, label: 'Equipment' },
            { key: 3, label: 'Research Assistant' },
            { key: 4, label: 'Others, please specify' },
        ],
    });

    const [defaultFormList] = useState({
        commercial: {
            value: '',
            label: 'Commercial / Non-commercial',
            name: 'commercial',
            type: 'boolean',
            trueValue: 'Commercial',
            falseValue: 'Non-commercial',
            isRequired: true,
        },
        type: {
            value: '',
            label: 'Type of Support',
            name: 'type',
            type: 'select',
            operators: choice['type'],
            validators: ['isRequired'],
        },
        name: {
            value: '',
            label: 'Name of organization / party',
            name: 'name',
            type: 'input',
            validators: ['isRequired'],
            maxLength: 255,
        },
        ownership: {
            value: '',
            label: 'Study data Ownership',
            name: 'ownership',
            type: 'boolean',
            isRequired: true,
        },
    });

    const getChoiceLabel = (type, key) => {
        return (
            choice[type]?.find((item) => {
                return item.key === key;
            })?.label ?? 'undefined'
        );
    };

    const handleEditClick = (row) => {
        setSelectedRow(row);
        setOpenPopup(true);
    };

    const handleRowCreate = async (id) => {
        setSelectedRow(null);
        setOpenPopup(true);
    };

    const onRecordUpdate = async (data) => {
        data.id = `temp_${generateRandomId()}`;

        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });
    };

    const onEditSubmit = (row) => {
        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );
    };

    const recoverDataCreation = async (item) => {
        // handle edited data recovery
        const updatedPostData = [];

        rows.forEach((item) => {
            const remoteDataObject = remoteData.find((remoteItem) => {
                return remoteItem.id === item.id;
            });

            if (remoteDataObject) {
                const isSame = Object.entries(item).every(([key, value]) => {
                    // remove exception
                    if (key === 'siteInfo' || key === 'applicationForm') {
                        return true;
                    }

                    return remoteDataObject[key] === value;
                });

                if (!isSame) {
                    updatedPostData.push(item);
                }
            }
        });

        const postData = rows.filter((row) => {
            const isDataCreation = row.id.toString().startsWith('temp_');

            if (!isDataCreation) {
                dispatch({
                    type: REMOVE_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        applicationFormId: formId,
                    },
                });
            }

            return isDataCreation;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...postData, ...updatedPostData],
                applicationFormId: formId,
            },
        });
    };

    const onDataEmpty = async (item) => {
        remoteData?.forEach((row) => {
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: serverTableKey,
                    id: row.id,
                    siteInfoId,
                    applicationFormId: formId,
                },
            });
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [],
                applicationFormId: formId,
            },
        });
    };

    const handleDelete = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                siteInfoId,
                applicationFormId: formId,
            },
        });
    };

    const getLabelName = (key) => {
        return defaultFormList[key]?.label ?? 'undefined';
    };

    const columns = [
        {
            field: 'commercial',
            headerName: getLabelName('commercial'),
            width: 150,
            valueGetter: (params) => {
                return params.value ? 'Commercial' : 'Non-commercial';
            },
        },
        {
            field: 'type',
            headerName: getLabelName('type'),
            width: 200,
            valueGetter: (params) => {
                return getChoiceLabel('type', params.value);
            },
        },
        { field: 'name', headerName: getLabelName('name'), width: 250 },
        {
            field: 'ownership',
            headerName: getLabelName('ownership'),
            width: 200,
            tooltip: 'Does the aforementioned organization/party has ownership of the study data?',
        },
    ];

    return {
        columns,
        rows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        selectedRow,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        setRows,
        handleDelete,
        onEditSubmit,
        onDataEmpty,
        recoverDataCreation,
        removeAllTempData,
    };
};

export default useNonMonetaryData;
